import { useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { Spinner } from '@/components/Spinner'
import { ArrowIcon, CheckIcon } from '@/components/icons'
import { TierBadge } from '@/components/TierBadge'
import { analyticsPathOnly, trackEvent } from '@/lib/analytics'
import { sanitizeRedirect, savePostLoginRedirect } from '@/lib/postLoginRedirect'
import { NET_BEWUST_SLUG, programsEnabled } from '@/lib/programs'
import { ProgramLoginPromo } from '@/components/programs/ProgramLoginPromo'

type Step = 'email' | 'check-email'

const PROGRAM_ROUTE = `/programs/${NET_BEWUST_SLUG}`

const BATTERY_USPS = [
  'Doorgerekend op je eigen slimme-meterdata, uur voor uur',
  'Vergelijk standaardgroottes van 2,5 t/m 30 kWh op besparing en terugverdientijd',
  'Download je persoonlijke rapport als PDF',
]

/** With programs live the page stays general; a program's details are behind the login. */
const USPS = programsEnabled
  ? [
      'Programma’s die je belonen voor slim laden en verbruik',
      'Batterijadvies: wat een thuisbatterij jou oplevert, uur voor uur doorgerekend',
      'Alles op je eigen slimme-meterdata, niet op aannames',
    ]
  : BATTERY_USPS

const STEPS = programsEnabled
  ? [
      { step: '1', title: 'Log in', text: 'Met het e-mailadres van je Chargee-account. Je data blijft van jou.' },
      {
        step: '2',
        title: 'Kies wat je wilt',
        text: 'Doe mee aan een programma, of laat een thuisbatterij doorrekenen op je eigen verbruik, opwek en dynamische prijzen.',
      },
      {
        step: '3',
        title: 'Jij beslist',
        text: 'Je ziet vooraf wat je verdient of bespaart. Meedoen en stoppen doe je zelf, wanneer je wilt.',
      },
    ]
  : [
      { step: '1', title: 'Log in', text: 'Met het e-mailadres van je Chargee-account. Je data blijft van jou.' },
      {
        step: '2',
        title: 'Wij rekenen door',
        text: 'We simuleren een thuisbatterij uur voor uur op je eigen verbruik, opwek en dynamische prijzen.',
      },
      {
        step: '3',
        title: 'Jij beslist',
        text: 'Je ziet per batterijgrootte wat je bespaart en wanneer die is terugverdiend.',
      },
    ]

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const loginWithEmail = useAuthStore((s) => s.loginWithEmail)
  const pollUntilComplete = useAuthStore((s) => s.pollUntilComplete)

  const [email, setEmail] = useState('')
  const [step, setStep] = useState<Step>('email')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const pollAbortRef = useRef<AbortController | null>(null)
  const loginCardRef = useRef<HTMLDivElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const toProgram = sanitizeRedirect(searchParams.get('redirect'))?.startsWith(PROGRAM_ROUTE) ?? false

  /** "Log in voor details": log in with the program as the target, from wherever the visitor is. */
  function joinProgram() {
    setSearchParams({ redirect: PROGRAM_ROUTE }, { replace: true })
    loginCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    emailRef.current?.focus({ preventScroll: true })
  }

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault()
    if (busy || !email.trim()) return
    setBusy(true)
    setError('')
    const redirect = sanitizeRedirect(searchParams.get('redirect'))
    // The magic link opens in a new tab without this query; keep the target for it.
    savePostLoginRedirect(redirect)
    trackEvent('login_email_submit', {
      has_redirect: Boolean(redirect),
    })
    try {
      const confirmToken = await loginWithEmail(email.trim())
      setStep('check-email')
      pollAbortRef.current = new AbortController()
      await pollUntilComplete(confirmToken, pollAbortRef.current.signal)
      const redirectPath = analyticsPathOnly(redirect) ?? '/services'
      trackEvent('login_success', {
        method: 'email',
        redirect_path: redirectPath,
      })
      navigate(redirect ?? '/services', { replace: true })
    } catch (err) {
      if (!pollAbortRef.current?.signal.aborted) {
        setError(
          err instanceof Error && err.message === 'Login timed out'
            ? 'De inloglink is verlopen. Probeer het opnieuw.'
            : 'Inloggen is niet gelukt. Controleer je e-mailadres en probeer het opnieuw.'
        )
        setStep('email')
      }
    } finally {
      setBusy(false)
    }
  }

  function cancelPolling() {
    pollAbortRef.current?.abort()
    setStep('email')
    setBusy(false)
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4">
      {/* Hero — asymmetric layout like the chargee.energy homepage */}
      <section className="mt-14 lg:mt-20">
        <h1 className="max-w-[820px] text-42 font-semibold leading-116 md:text-64 md:leading-106">
          {programsEnabled ? 'Haal meer uit je eigen energiedata' : 'Ontdek wat een thuisbatterij jou oplevert'}
        </h1>

        {/* Mobile-first: the login card comes right after the headline (flex-col-reverse) */}
        <div className="mt-6 flex flex-col-reverse justify-between gap-10 md:mt-3 md:flex-row md:items-start">
          <div className="max-w-[480px] md:pt-6">
            <span className="chip">
              <TierBadge tier="premium" className="!h-6 !px-2.5 !text-11" />
              Inbegrepen bij easyEnergy
            </span>
            <p className="mt-5 text-lg leading-144 text-dark-blue">
              {programsEnabled
                ? 'Log in met je Chargee-account voor je persoonlijke batterijrapport en programma’s die je belonen voor slim energiegebruik — op basis van jouw echte energiedata.'
                : 'Log in met je Chargee-account en ontvang direct je persoonlijke batterijrapport — geen aannames, maar jouw echte energiedata.'}
            </p>
            <ul className="mt-6 space-y-3">
              {USPS.map((usp) => (
                <li key={usp} className="flex items-start gap-3 text-base text-dark-blue">
                  <CheckIcon className="mt-0.5 size-5 shrink-0" />
                  {usp}
                </li>
              ))}
            </ul>

            {/* Battery report teaser with the thuisbatterij image from chargee.energy */}
            <div className="relative mt-8 hidden overflow-hidden rounded-3xl md:block">
              <img
                src="/thuisbatterij-spotlight.jpg"
                alt="Thuisbatterij"
                className="h-64 w-full object-cover"
              />
              <span className="absolute bottom-4 left-4 rounded-2xl bg-white/90 px-4 py-2.5 text-15 font-semibold text-dark-blue backdrop-blur">
                Jouw batterijrapport, op je eigen data
              </span>
            </div>
          </div>

          {/* Login card — the primary CTA of the page */}
          <div ref={loginCardRef} className="card w-full max-w-md shrink-0 scroll-mt-24 p-6 shadow-btn-shadow md:p-[30px]">
            {step === 'email' ? (
              <form onSubmit={submitEmail}>
                <h2 className="text-xl lg:text-26">
                  {programsEnabled ? 'Log in' : 'Log in en start je rapport'}
                </h2>
                <p className="mt-1.5 text-base leading-normal text-dark-blue/60">
                  Gebruik het e-mailadres van je Chargee-account. Je ontvangt een inloglink —
                  geen wachtwoord nodig.
                </p>
                <label className="mt-6 block">
                  <span className="field-label">E-mailadres</span>
                  <input
                    ref={emailRef}
                    type="email"
                    required
                    autoComplete="email"
                    className="input mt-1.5 w-full"
                    placeholder="jij@voorbeeld.nl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                {toProgram && (
                  <p className="mt-3 text-15 leading-normal text-dark-blue/70">
                    Na het inloggen zie je alle details en kun je je direct aanmelden.
                  </p>
                )}
                {error && <p className="mt-3 text-base font-semibold text-red">{error}</p>}
                <button
                  type="submit"
                  className="btn-primary mt-5 w-full !h-14 !text-base font-semibold"
                  disabled={busy}
                >
                  {busy ? <Spinner className="size-4" /> : null}
                  {programsEnabled ? 'Inloggen' : 'Log in en bekijk je rapport'}
                  {!busy && <ArrowIcon />}
                </button>
                <p className="mt-4 text-15 leading-normal text-text-gray">
                  Deze dienst is powered by Chargee: je logt in met het Chargee-account dat bij
                  je Sparky hoort. Nog geen Sparky? Kijk op{' '}
                  <a
                    href="https://www.easyenergy.com"
                    className="font-semibold text-blue hover-opacity"
                  >
                    easyenergy.com
                  </a>
                  .
                </p>
              </form>
            ) : (
              <div>
                <h2 className="text-xl lg:text-26">Check je inbox</h2>
                <p className="mt-2 text-base leading-normal text-dark-blue/60">
                  We hebben een inloglink gestuurd naar <strong>{email}</strong>. Klik op de link
                  in de e-mail; deze pagina logt daarna vanzelf in.
                </p>
                <div className="chip mt-5 w-full justify-center">
                  <Spinner className="size-4" />
                  Wachten op bevestiging…
                </div>
                <button className="btn-secondary mt-4 w-full" onClick={cancelPolling}>
                  Ander e-mailadres gebruiken
                </button>
              </div>
            )}
          </div>
        </div>

        {programsEnabled && <ProgramLoginPromo slug={NET_BEWUST_SLUG} onJoin={joinProgram} />}
      </section>

      {/* How it works — two-column section heading like chargee.energy */}
      <section className="mt-24 md:mt-32">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <h2 className="max-w-md text-28 font-semibold leading-111 lg:text-44">Zo werkt het</h2>
          <p className="max-w-96 text-base leading-6 text-dark-blue">
            {programsEnabled
              ? 'Van inloggen tot je eerste inzicht in een paar minuten — zonder dat je zelf iets hoeft aan te leveren.'
              : 'Van inloggen tot een persoonlijk batterijrapport in een paar minuten — zonder dat je zelf iets hoeft aan te leveren.'}
          </p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          {STEPS.map((item) => (
            <div key={item.step} className="card-beige p-5 md:p-[30px]">
              <span className="circle-btn text-15 font-semibold">{item.step}</span>
              <h3 className="mt-6 text-xl font-semibold">{item.title}</h3>
              <p className="mt-1.5 text-base leading-normal text-dark-blue/60">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Orange easyEnergy CTA block */}
      <section className="mt-24 md:mt-32">
        <div className="relative overflow-hidden rounded-3xl bg-orange px-6 py-16 md:px-14 md:py-20">
          <div className="relative z-10 max-w-xl">
            <h3 className="text-26 font-semibold leading-111 text-white md:text-44">
              Nog geen klant?
            </h3>
            <p className="mt-4 text-lg leading-144 text-white/90">
              Bij easyEnergy krijg je energie op basis van de échte beursprijs — en de Sparky
              P1-meter en app zijn inbegrepen. Daarmee krijg je realtime inzicht in je verbruik
              én dit batterijadvies op je eigen data.
            </p>
            <a
              href="https://www.easyenergy.com"
              className="btn-primary mt-8 !h-60 !px-8 !text-base"
            >
              stap nu direct over
              <ArrowIcon className="size-4" />
            </a>
          </div>
        </div>
      </section>

      <div className="pb-6" />
    </div>
  )
}
